import React from 'react';

import { mergeDeepRight } from 'ramda';
import { Path } from 'react-hook-form';

import { getDefaultValues } from 'api/defaultValues';
import { TSelectChangeHandler } from 'components/shared/BaseFormComponents/Select/types';
import { queryClient } from 'providers/AppQueryClientProvider';
import { isNotDefined } from 'utils/ramda';

import { useTripEventDefaultsLoader } from '../useTripEventDefaultValues';
import type { PageFieldValues, PendingPage, ProceedStatus, UseDefaultValuesProps } from './types';

export function useDefaultValues<T extends PageFieldValues>({
  methods,
  onResetData,
  eventType,
  customDataHandler,
}: UseDefaultValuesProps<T>) {
  const { getValues, setValue, reset } = methods;
  const attachedPage = getValues('attachedPage' as Path<T>);
  const [proceedStatus, setProceedStatus] = React.useState<ProceedStatus>('init');
  const [pendingPage, setPendingPage] = React.useState<PendingPage>(attachedPage);
  const page = pendingPage?.id || pendingPage?.name ? pendingPage : attachedPage;

  const maybeSetValue = React.useCallback(
    (key: string, value: any) => {
      if (value !== undefined) {
        setValue(key as Path<T>, value, { shouldDirty: true, shouldValidate: true });
      }
    },
    [setValue],
  );

  const { savedDefaultValues } = useTripEventDefaultsLoader({ eventType, page });

  const updateAttachedPage = React.useCallback(
    (newPage: NonNullable<PendingPage>) => {
      const { name, id, config } = newPage;
      const newAttachedPage =
        isNotDefined(id) && isNotDefined(name) ? undefined : ({ id, name, isCustom: config?.isCustom } as any);

      setValue('attachedPage' as Path<T>, newAttachedPage, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    },
    [setValue],
  );

  const getSavedDefaultValues = async (page: PendingPage) => {
    const { id: pageId, name: pageName } = page || {};
    if (!pageId && !pageName) return;

    try {
      const savedDefaultValues = await queryClient.fetchQuery({
        queryKey: ['defaultTripEventValues', pageId, pageName],
        queryFn: () => getDefaultValues(eventType, pageId, pageName),
        staleTime: 0,
      });

      return savedDefaultValues;
    } catch (error) {
      console.error('Error fetching default values:', error);
      return undefined;
    }
  };

  const handleDataReset = React.useCallback(async () => {
    if (!pendingPage) return;

    const currentValues = getValues();
    const resetedValues = onResetData(currentValues, pendingPage);
    const savedDefaultValues = await getSavedDefaultValues(pendingPage);

    /* If we have saved default values, we need to merge the data first */
    if (savedDefaultValues) {
      const mergedData = mergeDeepRight(resetedValues.data, savedDefaultValues?.data || {}) as T['data'];
      const handledData = customDataHandler ? customDataHandler(mergedData) : mergedData;

      reset({
        ...currentValues,
        icon: savedDefaultValues.icon,
        data: handledData,
      });
      return;
    }

    /* If we don't have saved default values, we need to reset the data */
    const handledValues = customDataHandler ? customDataHandler(resetedValues) : resetedValues;

    maybeSetValue('data', handledValues?.data);
    maybeSetValue('data.details', handledValues?.data?.details);
    maybeSetValue('data.rooming', handledValues?.data?.rooming);
    maybeSetValue('data.notes', handledValues?.data?.notes);
  }, [getValues, setValue, pendingPage, onResetData, reset, eventType]);

  /* Handle page selection */
  React.useEffect(() => {
    if (!pendingPage) return;

    updateAttachedPage(pendingPage);

    /* Only reset data if we're explicitly told to reset */
    if (proceedStatus === 'resetData') {
      handleDataReset();
    }
  }, [pendingPage, proceedStatus, updateAttachedPage, handleDataReset, page]);

  const keepData = React.useCallback((page: PendingPage) => {
    if (!page?.id) return;
    setProceedStatus('keepData');
    setPendingPage(page);
  }, []);

  const resetData = React.useCallback(() => {
    setProceedStatus('resetData');
  }, []);

  const handleAttachedPage: TSelectChangeHandler = React.useCallback((name, id, config) => {
    setProceedStatus('init');
    setPendingPage({ name, id, config });
  }, []);

  return {
    handleAttachedPage,
    keepData,
    resetData,
    setProceedStatus,
    proceedStatus,
    savedDefaultValues,
    getSavedDefaultValues,
  };
}
